import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  TrendingUp, 
  LogOut, 
  Menu, 
  X,
  BarChart3,
  Shield,
  Settings,
  Key,
  Server,
  Receipt,
  Wallet,
  DollarSign,
  MessageCircle,
  ArrowDownToLine,
  Mail
} from 'lucide-react';
import Logo from '../../components/Logo';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);

  useEffect(() => {
    // Check if admin is authenticated
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin-portal');
    }

    // Handle resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate]);

  // Poll for pending withdrawals
  useEffect(() => {
    const checkPendingWithdrawals = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
        if (!token) return;

        // Use relative URL - works on any domain
        const response = await fetch('/api/withdrawal-requests/pending', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setPendingWithdrawals(data.length);
        }
      } catch (error) {
        console.error('Error checking withdrawals:', error);
      }
    };

    checkPendingWithdrawals();
    const interval = setInterval(checkPendingWithdrawals, 15000); // Check every 15 seconds

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin-portal');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin-portal/dashboard' },
    { icon: Users, label: 'User Management', path: '/admin-portal/users' },
    { icon: MessageCircle, label: 'Support Messages', path: '/admin-portal/support-messages' },
    { icon: ArrowDownToLine, label: 'Withdrawals', path: '/admin-portal/withdrawals', badge: pendingWithdrawals },
    { icon: DollarSign, label: 'Deposit Management', path: '/admin-portal/deposits' },
    { icon: Mail, label: 'Email Campaign', path: '/admin-portal/email-campaign' },
    { icon: TrendingUp, label: 'Trade Analytics', path: '/admin-portal/trades' },
    { icon: Receipt, label: 'Transaction Control', path: '/admin-portal/transactions' },
    { icon: BarChart3, label: 'Trade Outcome Control', path: '/admin-portal/trade-control' },
    { icon: Settings, label: 'Basic Controls', path: '/admin-portal/controls' },
    { icon: Server, label: 'Platform Config', path: '/admin-portal/platform-controls' },
    { icon: Shield, label: 'KYC Management', path: '/admin-portal/kyc-management' },
    { icon: Wallet, label: 'Crypto Addresses', path: '/admin-portal/crypto-addresses' },
    { icon: Key, label: 'Passphrase Manager', path: '/admin-portal/passphrase' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex">
      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-50 h-screen bg-gradient-to-b from-slate-900 to-slate-800 border-r border-cyan-500/30 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-0 md:w-20'
        } overflow-hidden`}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-cyan-500/30">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-2">
                <Logo size="small" showText={false} />
                <div>
                  <h1 className="text-cyan-400 font-bold text-sm">ADMIN PORTAL</h1>
                  <p className="text-cyan-600 text-xs">Control Panel</p>
                </div>
              </div>
            )}
            {!sidebarOpen && !isMobile && (
              <Shield className="w-6 h-6 text-cyan-400 mx-auto" />
            )}
          </div>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-2 pb-24 overflow-y-auto" style={{maxHeight: 'calc(100vh - 240px)'}}>
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all relative ${
                isActive(item.path)
                  ? 'bg-gradient-to-r from-cyan-500/20 to-green-500/20 border border-cyan-500/50 text-cyan-400'
                  : 'text-cyan-600 hover:bg-slate-800 hover:text-cyan-400'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-semibold">{item.label}</span>}
              {item.badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 w-full p-4 border-t border-cyan-500/30">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 transition-all"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-semibold">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-slate-900/50 backdrop-blur-sm border-b border-cyan-500/30 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-cyan-400 text-sm font-semibold">Admin User</p>
                <p className="text-cyan-600 text-xs">Super Administrator</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
